import React, {
  FC,
  ReactElement,
  Fragment,
  useRef,
  MouseEvent
} from 'react';
import styles from '../EnvelopeItem.module.scss'
import cn from 'classnames';

import { useTranslation } from 'react-i18next';

import {
  Button,
  Ellipsis,
  Highlighter,
  Popover,
} from '@infologistics/frontend-libraries';

import { EnvelopeAllowedActionsName } from '@const/consts';

import { useAllowedActions } from '@hooks/envelopes/useAllowedActions';

import { IEnvelopeSubjectProps as IProps } from '@views/envelopes/types';
import { useAppSelector } from '@app/hooks/redux';
import { IRecipients } from '@store/modules/envelopes/types';
import EnvelopeLabels from '../../../EnvelopeLabels';

const getRecipientDisplayName = (
  recipient: IRecipients,
  canShowAssignedToBoxUserEmail: boolean
): string => {
  const { assignedToBoxUser } = recipient;

  if (assignedToBoxUser) {
    return canShowAssignedToBoxUserEmail &&
      assignedToBoxUser.email &&
      assignedToBoxUser.email !== recipient.email
      ? `${assignedToBoxUser.fullName} (${assignedToBoxUser.email})`
      : assignedToBoxUser.fullName;
  }

  return recipient.fullName ?? recipient.email ?? '';
};

const EnvelopeSubject: FC<IProps> = ({
  subject,
  recipients,
  oguid,
  boxOguid,
  direction,
  labels,
  availableLabels,
}) => {
  const { t } = useTranslation(['envelopes', 'common', 'envelope'])

  const { handleClickAllowedActions } = useAllowedActions()
  const searchContext = useAppSelector((state) => state.utils.searchContext)
  const currentBoxOguid = useAppSelector((state) => state.envelopes.currentBoxOguid)
  const canShowAssignedToBoxUserEmail = Boolean(boxOguid && boxOguid === currentBoxOguid)

  const searchContextCopy = [...searchContext];

  const buttonRef = useRef<HTMLButtonElement>(null)

  const recipientFirst = recipients[0] ? getRecipientDisplayName(recipients[0], canShowAssignedToBoxUserEmail) : ''
  const recipientSecond = recipients[1] ? `, ${getRecipientDisplayName(recipients[1], canShowAssignedToBoxUserEmail)}` : ''
  const recipientThird = recipients[2] ? getRecipientDisplayName(recipients[2], canShowAssignedToBoxUserEmail) : null

  const handleClick = (e: MouseEvent): void => {
    if (
      e.target instanceof Node &&
      buttonRef.current?.contains(e.target)
    ) return

    const option = { name: EnvelopeAllowedActionsName.OPEN }
    handleClickAllowedActions({ direction, option, oguid, boxOguid })
  }

  const getPopoverButtonCombonent = (): ReactElement =>
    <Button
      ref={buttonRef}
      classes='text-primary'
      linkStyle='none'
      theme='text'
    >
      {` + ${recipients.length - 2} ${t('common:more')}`}
    </Button>

  const getRecipients = (): ReactElement =>
    <span className={cn('grid relative grid-gap-x-none lh-2 align-items-baseline', styles.recipients)}>
      <Ellipsis externalClass='mr-1 text-muted'>
        {`${t('envelopes:to')}: ${recipientFirst}${recipientSecond}`}
      </Ellipsis>
      {
        recipientThird && (
          <Fragment>
            <Popover
              buttonComponent={getPopoverButtonCombonent()}
              containerClasses={cn(styles.tooltip, 'p-2')}
              buttonClasses='d-flex'
              openButtonClasses='d-flex'
              withoutArrow
            >
              {
                <ul className="m-0 pl-4 text-nowrap text-color">
                  {
                    recipients.map((recipient, index) =>
                      <li
                        key={index}
                        className={cn('font-xs relative', styles.tooltip_item)}
                      >
                        { getRecipientDisplayName(recipient, canShowAssignedToBoxUserEmail) }
                      </li>
                    )
                  }
                </ul>
              }
            </Popover>
          </Fragment>
        )
      }
    </span>

  return (
    <td
      className={cn(styles.subject_container, 'pointer')}
      onClick={handleClick}
    >
      <div className='d-flex align-items-center'>
        <div className='grid full-width grid-gap-y-none'>
          <Highlighter searchStrArray={searchContextCopy}>
            <Ellipsis>{subject}</Ellipsis>
            { getRecipients() }
          </Highlighter>
          <EnvelopeLabels labels={labels} availableLabels={availableLabels} />
        </div>
      </div>
    </td>
  )
}

export default EnvelopeSubject
